import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useNavigate } from 'react-router-dom';
import useMount from '../../../../../common/hooks/useMount';
import Breadcrumbs from '../../../../../common/widgets/Breadcrumbs';
import Button from '../../../../../common/widgets/Button';
import Icons from '../../../../../common/widgets/Icons';
import Loader from '../../../../../common/widgets/Loader';
import { rewriteImageUrl } from '../../../helpers';
import { HelpSectionItemModel } from '../../../models/Help';
import { resources } from '../../../resources/resources';
import { helpService } from '../../../services/help';
import applicationCrumbs from '../../ApplicationCrumbs';

interface HelpSectionsPageState {
  sections?: HelpSectionItemModel[];
  showErrorPanel?: boolean;
}

export const HelpSectionsPage = () => {
  const { t: translate } = useTranslation();
  const crumbs = applicationCrumbs(translate);
  const [state, setState] = useState<HelpSectionsPageState>({});

  const loadSections = async (abortController?: AbortController) => {
    const resp = await helpService.getHelpSections({
      signal: abortController?.signal,
    });
    if (!resp.hasErrors) {
      setState({ sections: resp.data });
    } else {
      if (!abortController?.signal.aborted) {
        setState({ showErrorPanel: true });
      }
    }
  };

  useMount(loadSections);

  return (
    <>
      <Breadcrumbs entries={[crumbs.home, crumbs.help]} />
      <section className="help-page-header">
        <span
          className="help-header-shape help-header-shape-left"
          aria-hidden="true"
        />
        <span className="help-header-photo" aria-hidden="true" />
        <span className="help-header-glow" aria-hidden="true" />
        <span
          className="help-header-shape help-header-shape-right-top"
          aria-hidden="true"
        />
        <span
          className="help-header-shape help-header-shape-right-bottom"
          aria-hidden="true"
        />
        <div className="container">
          <h1>{translate(resources.help.title)}</h1>
          <span>{translate(resources.help.subtitle)}</span>
        </div>
      </section>
      <section className="section-main section-full help-main-page">
        <div className="container">
          <Loader
            loading={!state.sections}
            loader={<Skeleton />}
            showErrorPanel={state.showErrorPanel}
          >
            <div className="help-cards-list ">
              {state.sections?.map(e => (
                <HelpSectionTile key={e.sectionId} {...e} />
              ))}
            </div>
          </Loader>
        </div>
      </section>
    </>
  );
};

interface HelpSectionTileProps extends HelpSectionItemModel {}

const customSectionIcons: Record<string, () => React.JSX.Element> = {
  buget: Icons.HelpCardBudget,
  cont: Icons.HelpCardAccount,
  'evenimente speciale': Icons.HelpCardSpecialEvents,
  vouchere: Icons.HelpCardVouchers,
};

const stripTags = (str: string) => {
  let result = '';
  let inTag = false;

  for (const ch of str) {
    if (ch === '<') {
      inTag = true;
    } else if (ch === '>') {
      inTag = false;
    } else if (!inTag) {
      result += ch;
    }
  }

  return result;
};

const getCustomSectionIcon = (name: string) => {
  const plainName = stripTags(name).trim().toLowerCase();
  return customSectionIcons[plainName];
};

const HelpSectionTile = (props: HelpSectionTileProps) => {
  const { sectionId, name, icon, questions, isGuidesAndResourcesSection } =
    props;
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const CustomIcon = getCustomSectionIcon(name);

  return (
    <div className="help-card">
      <div className="help-card-title">
        <span>
          {CustomIcon ? (
            <CustomIcon />
          ) : (
            <img src={rewriteImageUrl(icon)} alt="" />
          )}
        </span>
        <h2
          dangerouslySetInnerHTML={{
            __html: name,
          }}
        ></h2>
      </div>
      <ul className="list-reset">
        {questions
          .filter(e => !!e)
          .map(e => (
            <li
              className={isGuidesAndResourcesSection ? 'no-icon' : ''}
              key={e.questionId}
            >
              <button
                className="cursor-pointer btn-no-style"
                onClick={() =>
                  navigate(`/help/sections/${sectionId}#${e.questionId}`)
                }
                dangerouslySetInnerHTML={{
                  __html: e.question,
                }}
              />
            </li>
          ))}
      </ul>

      <div className="help-card-action">
        <Button
          onClick={() => navigate(`/help/sections/${sectionId}`)}
          className="btn btn-outline btn-full"
        >
          <span className="btn-text">
            {translate(
              isGuidesAndResourcesSection
                ? resources.help.seeResources
                : resources.help.otherQuestions,
            )}
          </span>
        </Button>
      </div>
    </div>
  );
};
